/**
 * PWA Install Prompt Hook
 * Handles beforeinstallprompt event and provides install functionality
 */

import { useState, useEffect } from "react";
import { log } from "@/lib/log";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface UsePWAInstallReturn {
  canInstall: boolean;
  isInstalled: boolean;
  promptInstall: () => Promise<void>;
  dismissPrompt: () => void;
}

export function usePWAInstall(): UsePWAInstallReturn {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    // Check if installed as iOS PWA
    if (
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
    ) {
      setIsInstalled(true);
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setCanInstall(true);

      // Store that prompt was shown
      localStorage.setItem("pwa-install-prompt-shown", "true");
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
      localStorage.setItem("pwa-installed", "true");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Check if user already dismissed the prompt
    const promptDismissed = localStorage.getItem(
      "pwa-install-prompt-dismissed",
    );
    if (promptDismissed) {
      const dismissedTime = parseInt(promptDismissed);
      const daysSinceDismissed =
        (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

      // Show prompt again after 7 days
      if (daysSinceDismissed < 7) {
        setCanInstall(false);
      }
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show install prompt
    await deferredPrompt.prompt();

    // Wait for user choice
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      log.debug("User accepted PWA install");
      setIsInstalled(true);
    } else {
      log.debug("User dismissed PWA install");
    }

    // Clear deferred prompt
    setDeferredPrompt(null);
    setCanInstall(false);
  };

  const dismissPrompt = () => {
    setCanInstall(false);
    localStorage.setItem("pwa-install-prompt-dismissed", Date.now().toString());
  };

  return {
    canInstall,
    isInstalled,
    promptInstall,
    dismissPrompt,
  };
}
